import { useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { ArrowLeft, Sparkles, CalendarDays, Video, RotateCcw } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { MavisApiError, getMeeting } from '../../lib/mavisApi';
import type { MavisMeetingSession } from '../../types/mavis';
import { MavisSessionWorkspace } from '../../components/mavis/MavisMeetingPanel';
import MavisLivePlaybook from '../../components/mavis/MavisLivePlaybook';
import MavisMeetingAI from '../../components/mavis/MavisMeetingAI';
import { meetingDate, meetingDateKey, meetingDayLabel, meetingTime } from '../../components/mavis/meetingDirectoryModel';

const MEETING_UNAVAILABLE = 'This meeting is unavailable or you no longer have access.';
const MEETING_LOAD_FAILED = 'This meeting could not be loaded. Please retry.';

class MeetingUnavailableError extends Error {}

function LoadingSkeleton() {
  return (
    <div aria-label="Loading meeting" role="status" className="space-y-4 motion-safe:animate-pulse">
      <div className="h-7 bg-app-line/50 rounded-lg w-2/5" />
      <div className="h-4 bg-app-line/40 rounded-lg w-1/4" />
      <div className="h-64 rounded-xl border border-app-line bg-app-canvas" />
    </div>
  );
}

function LoadError({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <section className="card p-8 text-center">
      <Video className="w-6 h-6 text-app-muted mx-auto mb-3" />
      <p role="alert" className="text-sm text-app-muted">{message}</p>
      <button className="btn-secondary !text-sm mt-4" onClick={onRetry}>
        <RotateCcw className="w-4 h-4" />
        Retry
      </button>
    </section>
  );
}

function loadErrorMessage(err: unknown): string {
  if (err instanceof MeetingUnavailableError) return MEETING_UNAVAILABLE;
  // 400 is a malformed meeting id in the URL: retrying cannot help, so treat it like a missing meeting.
  if (err instanceof MavisApiError) return err.status === 404 || err.status === 400 ? MEETING_UNAVAILABLE : MEETING_LOAD_FAILED;
  return err instanceof Error ? err.message : 'Meeting unavailable.';
}

export default function MavisMeetingDetail() {
  const { opportunityId, meetingId } = useParams();
  const { organisation } = useAuth();
  const location = useLocation();
  const [session, setSession] = useState<MavisMeetingSession | null>(null);
  const [opportunityTitle, setOpportunityTitle] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [aiOpen, setAiOpen] = useState(false);

  const fallback = `/app/mavis/${opportunityId}?tab=meeting_control`;
  const returnTo = typeof location.state?.returnTo === 'string' && location.state.returnTo.startsWith(`/app/mavis/${opportunityId}?`)
    ? location.state.returnTo
    : fallback;

  const load = useCallback(async () => {
    if (!organisation || !meetingId || !opportunityId) return;
    setLoading(true);
    setError('');
    try {
      const { session: meeting, opportunity } = await getMeeting(meetingId);
      // The page URL carries both ids; only show the meeting under its own opportunity and org.
      const belongsHere = meeting?.opportunity_id === opportunityId
        && meeting.org_id === organisation.id
        && opportunity?.id === opportunityId;
      if (!belongsHere) throw new MeetingUnavailableError();
      setSession(meeting as MavisMeetingSession);
      setOpportunityTitle(opportunity.title);
    } catch (err) {
      setError(loadErrorMessage(err));
      setSession(null);
    } finally {
      setLoading(false);
    }
  }, [organisation?.id, meetingId, opportunityId]);

  useEffect(() => {
    void load();
  }, [load]);

  const ended = session && ['completed', 'ended', 'left', 'failed'].includes(session.status);
  const search = new URLSearchParams(location.search);
  const initialBotId = search.get('bot') ?? undefined;
  const initialTime = search.has('at') ? Number(search.get('at')) : undefined;

  return (
    <div className="max-w-[1440px] mx-auto px-4 sm:px-6 py-6 space-y-5">
      <Link to={returnTo} className="inline-flex items-center gap-2 text-sm text-app-muted hover:text-app-ink">
        <ArrowLeft className="w-4 h-4" />
        Back to meetings
      </Link>
      {loading ? (
        <LoadingSkeleton />
      ) : error ? (
        <LoadError message={error} onRetry={() => void load()} />
      ) : session && (
        <>
          <header className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <p className="text-xs text-app-muted mb-1.5">{opportunityTitle}</p>
              <h1 className="text-2xl font-semibold tracking-tight text-app-ink">{session.meeting_title}</h1>
              <p className="mt-2 flex items-center gap-2 text-xs text-app-muted">
                <CalendarDays className="w-3.5 h-3.5" />
                {meetingDayLabel(meetingDateKey(meetingDate(session)))} · {meetingTime(session)} · {Intl.DateTimeFormat().resolvedOptions().timeZone}
              </p>
            </div>
            <button
              className="btn-primary !text-sm"
              disabled={!ended}
              title={!ended ? 'Available after the call ends' : undefined}
              onClick={() => setAiOpen(true)}
            >
              <Sparkles className="w-4 h-4" />
              Ask MAVIS
            </button>
          </header>
          <div className="rounded-xl border border-app-line bg-app-surface overflow-hidden">
            <MavisSessionWorkspace
              key={session.id}
              session={session}
              onUpdated={setSession}
              initialBotId={initialBotId}
              initialTime={initialTime}
              liveInsights={!ended ? <MavisLivePlaybook meetingId={session.id} opportunityId={opportunityId!} live /> : undefined}
              playbook={<MavisLivePlaybook meetingId={session.id} opportunityId={opportunityId!} />}
            />
          </div>
          <MavisMeetingAI key={session.id} meetingId={session.id} open={aiOpen} onClose={() => setAiOpen(false)} />
        </>
      )}
    </div>
  );
}
